import {
	applyLookup,
	findUniqueIds,
	getLookupSourceValue,
	isValidObjectId,
	LookupConfig,
} from "../find_unique_ids_in_list";
import {
	addResponsibleNames,
	collectPeopleEmails,
	fetchEmailToNameMap,
} from "../responsibleNames";
import { getReportAuthorEmail } from "./reportAuthor";
import { ReportItemList } from "./reportEditAccess";
import { getStreamNameFor, toStreamEmails } from "./reportStream";

// A stream's Export PDF builds the PDF the report's page exports, for the one
// report it is asked for.

type ItemListKey = ReportItemList["name"];

// The fields of a report its PDF's names come from.
export type ExportedReport = {
	reviewer?: unknown;
	primary_author?: string | null;
	author?: string | null;
};

const getItems = (report: Record<string, any>, listKey: ItemListKey): any[] =>
	Array.isArray(report[listKey]) ? report[listKey] : [];

// A person's name in a field, when one is found for their email. Otherwise
// nothing, as a page's lookup leaves the field for the PDF.
export function withFoundName(
	field: string,
	email: unknown,
	emailToName: Record<string, string>
) {
	const name = getStreamNameFor(emailToName, email);
	return name === undefined ? {} : { [field]: name };
}

// Everyone a report's PDF names: its reviewer, its author, and each item's
// responsible person and, where recorded, its completer. A report type
// without items, such as a lesson, has no list.
export const collectReportExportEmails = (
	report: ExportedReport & Record<string, any>,
	listKey?: ItemListKey
) =>
	toStreamEmails([
		report.reviewer,
		getReportAuthorEmail(report),
		...(listKey ? collectPeopleEmails(getItems(report, listKey)) : []),
	]);

// The report as its PDF reads it, with the names found: the reviewer and the
// author, and each item's people when it has a list.
export function getReportExportData<T extends ExportedReport>(
	report: T,
	emailToName: Record<string, string>,
	listKey?: ItemListKey
) {
	return {
		...report,
		...withFoundName("reviewer_name", report.reviewer, emailToName),
		...withFoundName("author_name", getReportAuthorEmail(report), emailToName),
		...(listKey
			? {
					[listKey]: addResponsibleNames(
						getItems(report, listKey),
						emailToName
					),
				}
			: {}),
	};
}

// Whether a report's field holds an id a lookup can find: ObjectId text, or
// a list with at least one.
function holdsLookupId(report: Record<string, any>, config: LookupConfig) {
	const value = getLookupSourceValue(report, config);
	return Array.isArray(value)
		? value.some(isValidObjectId)
		: isValidObjectId(value);
}

// A page's lookup configs run for one report, as the page's lookup gives its
// export the names of the report's project, well, department and so on. Only
// the configs whose field holds an id are fetched. findUniqueIds fetches each
// config even with no ids, and most requests lose their filter with an empty
// list, so they read every record of that kind: every user for the
// email-keyed configs, whose names come from getReportExportData instead.
export async function lookUpReportForExport<T extends Record<string, any>>(
	report: T,
	configs: LookupConfig[]
): Promise<T> {
	const kept = configs.filter((config) => holdsLookupId(report, config));
	if (kept.length === 0) return report;
	const lookupData = await findUniqueIds([report], kept);
	return applyLookup([report], lookupData, kept)[0];
}

// The names for a PDF's emails. None when the lookup fails, so the PDF
// prints the emails, as a page's export does when its lookup fails.
export async function fetchExportNames(
	emails: string[],
	lookUpNames: (
		emails: string[]
	) => Promise<Record<string, string>> = fetchEmailToNameMap
): Promise<Record<string, string>> {
	try {
		return await lookUpNames(emails);
	} catch (error) {
		// The message only: a request error carries the bearer token.
		console.error(
			"Failed to look up the names for the report's PDF:",
			error instanceof Error ? error.message : error
		);
		return {};
	}
}
